<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Overpayment" avatarImage="/glass/img/accounts-overview-overpayment.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Over Payment #{{overPayment.sequenceNumber}}</h4>

          <template v-if="hasIntegrations">
            <h5>Integrations</h5>
            <p>
              <LinkIntegrationLink v-for="integration in integrationList" :key="integration.code" :integration="integration" />
            </p>
          </template>

          <OverviewBoxInfoItem v-if="overPayment.contact" icon="fa-user-circle">
            <LinkContact :id="overPayment.contactId" :name="overPayment.contact.name" />
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="overPayment.paymentReceived" icon="fa-credit-card">
            <LinkPaymentReceived :id="overPayment.paymentReceived.id" :name="overPayment.paymentReceived.sequenceNumber" />
          </OverviewBoxInfoItem>

          <OverviewBoxInfoItem icon="fa-tag" v-if="overPayment.reference">
            {{overPayment.reference}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-tag" v-if="overPayment.description">
            {{overPayment.description}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle">
            {{overPayment.amount | formatCurrency}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-envelope-open-dollar" v-if="overPayment.totalReservedAmount > 0">
            {{overPayment.totalReservedAmount | formatCurrency}} Reserved
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-check-circle">
            <span :class="{'financial-profit': hasAvailableAmount}">{{availableAmount | formatCurrency}} Available</span>
          </OverviewBoxInfoItem>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <LabelBadge :labelClass="`label-payment-status-${overPayment.status}`">{{overPayment.status | formatLookupName(OverpaymentStatusTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="overPayment.sequenceNumber">{{overPayment.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{overPayment.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{overPayment.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-payment-summary">
              <i class="far fa-tasks"></i> Summary
            </Tab>
            <Tab tabid="tab-payment-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-payment-summary">
              <div v-if="overPayment.id">
                <DataPageArea title="Details">
                  <template slot="actionArea">
                    <ActionDropMenu class="float-right">
                      <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AccountsPermissionTypes.payment) && isAllocationAvailable" @click="allocateOverPayment" icon="fa-plus">Allocate Overpayment</DropdownMenuItem>
                      <DropdownMenuItem @click="closeOverpayment()" icon="fa-check-circle" v-if="isOverpaymentClosable">Close Overpayment</DropdownMenuItem>
                    </ActionDropMenu>
                  </template>
                  <template slot="dataArea">
                    <DataItem title="Payment Number">{{overPayment.sequenceNumber}}</DataItem>
                    <DataItem title="Description">{{overPayment.description}}</DataItem>
                    <DataItem title="Reference">{{overPayment.reference}}</DataItem>
                    <DataItem title="Original Amount">{{overPayment.amount | formatCurrency}}</DataItem>
                    <DataItem title="Reserved Amount">{{overPayment.totalReservedAmount | formatCurrency}}</DataItem>
                    <DataItem title="Available Amount">{{availableAmount | formatCurrency}}</DataItem>
                    <DataItem title="Date Created">{{overPayment.dateCreated | formatDateTime}}</DataItem>
                  </template>
                </DataPageArea>

                <div class="row mt-4">
                  <div class="col-12">
                    <ContentTitle>Order Reserved Payments</ContentTitle>
                    <ContentSubTitle>Orders this overpayment is reserved against pending invoicing</ContentSubTitle>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Order</th>
                          <th>Order Status </th>
                          <th>Reserved Date</th>
                          <th>Order Amount</th>
                          <th>Reserved Amount</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="reservedPayment in overPayment.orderReservedPayments" :key="reservedPayment.id">
                          <td class="font-weight-bold">
                            <LinkOrder :id="reservedPayment.orderId" :name="reservedPayment.order.sequenceNumber" />
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-order-status-${reservedPayment.order.status}`">{{reservedPayment.order.status | formatLookupName(OrderStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>{{reservedPayment.dateReserved | formatDateTime}}</td>
                          <td>{{reservedPayment.order.totalPrice | formatCurrency}}</td>
                          <td>{{reservedPayment.reservedAmount | formatCurrency}}</td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="!hasOrderReservedPayments">
                        No payments reserved for orders
                      </template>
                    </StaticDataTable>
                  </div>
                </div>

                <div class="row mt-4">
                  <div class="col-12">
                    <ContentTitle>Invoice Allocations</ContentTitle>
                    <ContentSubTitle>Invoices this overpayment has been allocated to</ContentSubTitle>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Invoice</th>
                          <th>Invoice Status </th>
                          <th>Payment Status</th>
                          <th>Payment Date</th>
                          <th>Invoice Amount</th>
                          <th>Payment Amount</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="invoicePayment in overPayment.invoicePaymentAllocations" :key="invoicePayment.id">
                          <td class="font-weight-bold">
                            <LinkInvoice :id="invoicePayment.invoiceId" :name="invoicePayment.invoice.sequenceNumber" />
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-invoice-status-${invoicePayment.invoice.status}`">{{invoicePayment.invoice.status | formatLookupName(InvoiceStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-payment-status-${invoicePayment.status}`">{{invoicePayment.status | formatLookupName(PaymentStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>{{invoicePayment.dateAllocated | formatDateTime}}</td>
                          <td>{{invoicePayment.invoice.totalPrice | formatCurrency}}</td>
                          <td class="font-weight-bold">{{invoicePayment.allocatedAmount | formatCurrency}}</td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="!hasInvoicePaymentAllocations">
                        No invoice allocations made
                      </template>
                    </StaticDataTable>
                  </div>
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-payment-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.overPayment" :linkId="overPayment.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'

import OverPaymentAllocationDialog from '@/modules/accounts/views/overPaymentAllocationDialog'
import OverPaymentCloseDialog from '@/modules/accounts/views/overPaymentCloseDialog'

import { OrderStatusTypeLookup, InvoiceStatusTypeLookup, PaymentStatusTypes, PaymentStatusTypeLookup, OverpaymentStatusTypes, OverpaymentStatusTypeLookup, PaymentTypes, PaymentTypeLookup, LinkTypes } from '@/store/types'


export default {
  name: 'OverPayment',
  mixins: [form, pdfMixin],
  computed: {
    activityStreamURL() {
      if(!this.$data.overPaymentId)
        return null
      return `/accounts/overpayments/${this.$data.overPaymentId}/activitystream`
    },
    hasIntegrations() {
      return this.integrationList.filter(integration => integration.active || integration.pending).length > 0
    },
    integrationList() {
      if(this.$data.overPayment.integrationData == null)
        return []
      return Object.values(this.$data.overPayment.integrationData)        
    },
    hasInvoicePaymentAllocations() {
      if(this.$data.overPayment.invoicePaymentAllocations == null)
        return false
      return this.$data.overPayment.invoicePaymentAllocations.length > 0
    },
    hasOrderReservedPayments() {
      if(this.$data.overPayment.orderReservedPayments == null)
        return false
      return this.$data.overPayment.orderReservedPayments.length > 0
    },
    isAllocationAvailable() {
      return this.$data.overPayment.status == OverpaymentStatusTypes.unallocated
    },
    isOverpaymentClosable() {
      if(this.overPayment.status == OverpaymentStatusTypes.unallocated && this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.accountAdministrator))
        return true
      return false
    },
    availableAmount() {
      return this.overPayment.remainingCredit - this.overPayment.totalReservedAmount
    },
    hasAvailableAmount() {
      return this.availableAmount > 0
    }
  },
  data() {
    return {
      overPaymentId: null,
      overPayment: {}
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
    this.InvoiceStatusTypeLookup = InvoiceStatusTypeLookup
    this.PaymentStatusTypeLookup = PaymentStatusTypeLookup
    this.OverpaymentStatusTypeLookup = OverpaymentStatusTypeLookup
    this.PaymentTypeLookup = PaymentTypeLookup    
  },
  mounted() {
    this.$data.overPaymentId = this.$route.params.id

    // Load the payment received
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.$data.overPaymentId = this.$route.params.id

    this.pageRefreshed()
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.accountsService.getOverPayment(this.$data.overPaymentId),
      ]))
      .then(results => {
        this.overPayment = results[0].data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    allocateOverPayment() {
      this.displayModal(OverPaymentAllocationDialog, {
        overPaymentId: parseInt(this.$data.overPaymentId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Overpayment Allocated')
          this.reloadData()
        }
      })    
    },
    closeOverpayment() {
      this.displayModal(OverPaymentCloseDialog, {
        overPaymentId: parseInt(this.$data.overPaymentId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Overpayment Updated')
          this.reloadData()
        }
      })
    }
  }
}
</script>
